import { useContext, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Select from 'react-select';
import { Country, State } from 'country-state-city';
import useApi from '../hooks/useApi';
import { AuthContext } from '../context/AuthContext';
import Modal from '../components/Modal';

const WasteRequests = () => {
  const { data: requests, isLoading, error } = useApi('/waste/waste-requests');
  const [filteredRequests, setFilteredRequests] = useState([]);
  const { state } = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useContext(AuthContext);

  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);

  const BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

  const [filters, setFilters] = useState({
    searchQuery: state?.searchQuery || '',
    type: '',
    status: 'bidding', // Default to Bidding
    country: '',
    state: '',
    dateFrom: '',
    dateTo: '',
  });

  const [availableStates, setAvailableStates] = useState([]);

  const typeOptions = [
    { value: 'fertilizer', label: 'Fertilizer' },
    { value: 'feed', label: 'Feed' },
    { value: 'energy', label: 'Energy' },
    { value: 'compost', label: 'Compost' },
    { value: 'biogas', label: 'Biogas' },
    { value: 'biomass', label: 'Biomass' },
    { value: 'recycling', label: 'Recycling' },
    { value: 'organic_waste', label: 'Organic Waste' },
    { value: 'poultry_litter', label: 'Poultry Litter' },
    { value: 'soil_conditioner', label: 'Soil Conditioner' },
    { value: 'manure_processing', label: 'Manure Processing' },
    { value: 'waste_to_energy', label: 'Waste to Energy' },
    { value: 'agricultural_reuse', label: 'Agricultural Reuse' },
    { value: 'feather_recycling', label: 'Feather Recycling' },
    { value: 'bedding_waste', label: 'Bedding Waste' },
    { value: 'industrial_recycling', label: 'Industrial Recycling' },
    { value: 'sustainable_disposal', label: 'Sustainable Disposal' },
  ];

  const statusOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'bidding', label: 'Bidding' },
    { value: 'canceled', label: 'Canceled' },
    // 'completed' and 'accepted' removed as per requirement
  ];

  const countryOptions = Country.getAllCountries().map((country) => ({
    value: country.isoCode,
    label: country.name,
  }));

  const stateOptions = availableStates.map((state) => ({
    value: state.isoCode,
    label: state.name,
  }));

  const formatDate = (date) => (date ? new Date(date).toLocaleDateString() : 'N/A');

  const handleResetFilters = () => {
    setFilters({
      searchQuery: '',
      type: '',
      status: 'bidding', // Reset to Bidding
      country: '',
      state: '',
      dateFrom: '',
      dateTo: '',
    });
  };

  useEffect(() => {
    let filtered = requests || [];

    // Only show bidding requests (as per new requirement)
    filtered = filtered.filter((req) => req.status === 'bidding');

    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      filtered = filtered.filter(
        (req) =>
          req.title?.toLowerCase().includes(query) ||
          req.description?.toLowerCase().includes(query) ||
          req.type?.toLowerCase().includes(query) ||
          req.location?.address?.toLowerCase().includes(query) ||
          req.location?.city?.toLowerCase().includes(query)
      );
    }

    if (filters.type) {
      filtered = filtered.filter((req) => req.type === filters.type);
    }

    if (filters.status) {
      filtered = filtered.filter((req) => req.status === filters.status);
    }

    if (filters.country) {
      filtered = filtered.filter((req) => req.location?.country === filters.country);
    }

    if (filters.state) {
      filtered = filtered.filter((req) => req.location?.state === filters.state);
    }

    if (filters.dateFrom) {
      filtered = filtered.filter((req) => new Date(req.createdAt) >= new Date(filters.dateFrom));
    }

    if (filters.dateTo) {
      filtered = filtered.filter((req) => new Date(req.createdAt) <= new Date(filters.dateTo));
    }

    setFilteredRequests(filtered);
  }, [requests, filters]);

  useEffect(() => {
    if (filters.country) {
      const states = State.getStatesOfCountry(filters.country).map((state) => ({
        isoCode: state.isoCode,
        name: state.name,
      }));
      setAvailableStates(states);
      setFilters((prev) => ({ ...prev, state: '' }));
    } else {
      setAvailableStates([]);
      setFilters((prev) => ({ ...prev, state: '' }));
    }
  }, [filters.country]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({ ...filters, [name]: value });
  };

  const handleSelectChange = (name) => (selected) => {
    setFilters({ ...filters, [name]: selected ? selected.value : '' });
  };

  const handlePlaceBid = (request) => {
    if (!isAuthenticated()) {
      setIsLoginModalOpen(true);
      return;
    }
    if (user.role !== 'waste_collector') {
      setIsRoleModalOpen(true);
      return;
    }
    navigate(`/waste-requests/${request._id}`, { state: { request } });
  };

  const handleLoginClose = () => {
    setIsLoginModalOpen(false);
    navigate('/login');
  };

  const handleRoleClose = () => {
    setIsRoleModalOpen(false);
  };

  const handleRoleRedirect = () => {
    setIsRoleModalOpen(false);
    navigate('/signup');
  };

  if (isLoading) {
    return (
      <div className="text-center text-gray-600 text-lg font-medium py-12">
        Loading waste requests...
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center text-red-600 text-lg font-medium py-12">
        Error loading waste requests: {error.message}
      </div>
    );
  }

  return (
    <div className="relative w-full max-w-7xl mx-auto py-12 sm:py-16 px-4 sm:px-6 lg:px-8">
      <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-primary text-center mb-8 tracking-tight bg-gradient-to-r from-secondary/20 to-transparent rounded-lg py-3">
        Waste Requests
      </h1>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Filters Sidebar */}
        <div className="w-full lg:w-1/4 bg-white rounded-xl shadow-xl p-6 sm:p-8 border border-secondary/10">
          <div className="flex justify-between items-center mb-6 sm:mb-8">
            <h2 className="text-xl sm:text-2xl font-semibold text-primary">Filters</h2>
            <button
              onClick={handleResetFilters}
              className="flex items-center gap-1 text-secondary text-sm sm:text-base font-medium hover:text-opacity-90 transition duration-200"
            >
              <span>↺</span> Reset
            </button>
          </div>

          <div className="space-y-6">
            <div>
              <label className="block text-gray-700 font-medium mb-2 text-sm sm:text-base">Search</label>
              <input
                type="text"
                name="searchQuery"
                placeholder="Search by title, description, or location"
                value={filters.searchQuery}
                onChange={handleFilterChange}
                className="w-full p-2.5 sm:p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-gray-700 font-medium mb-2 text-sm sm:text-base">Type</label>
              <Select
                options={typeOptions}
                value={typeOptions.find((opt) => opt.value === filters.type) || null}
                onChange={handleSelectChange('type')}
                placeholder="All Types"
                isClearable
                className="w-full"
                classNamePrefix="react-select"
              />
            </div>

            <div>
              <label className="block text-gray-700 font-medium mb-2 text-sm sm:text-base">Status</label>
              <Select
                options={statusOptions}
                value={statusOptions.find((opt) => opt.value === filters.status) || null}
                onChange={handleSelectChange('status')}
                placeholder="All Statuses"
                isClearable
                className="w-full"
                classNamePrefix="react-select"
              />
            </div>

            <div>
              <label className="block text-gray-700 font-medium mb-2 text-sm sm:text-base">Country</label>
              <Select
                options={countryOptions}
                value={countryOptions.find((opt) => opt.value === filters.country) || null}
                onChange={handleSelectChange('country')}
                placeholder="All Countries"
                isClearable
                className="w-full"
                classNamePrefix="react-select"
              />
            </div>

            <div>
              <label className="block text-gray-700 font-medium mb-2 text-sm sm:text-base">State</label>
              <Select
                options={stateOptions}
                value={stateOptions.find((opt) => opt.value === filters.state) || null}
                onChange={handleSelectChange('state')}
                placeholder="All States"
                isClearable
                isDisabled={!filters.country}
                className="w-full"
                classNamePrefix="react-select"
              />
            </div>

            <div>
              <label className="block text-gray-700 font-medium mb-2 text-sm sm:text-base">Date Range</label>
              <div className="flex gap-3">
                <input
                  type="date"
                  name="dateFrom"
                  value={filters.dateFrom}
                  onChange={handleFilterChange}
                  className="w-1/2 p-2.5 sm:p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent"
                />
                <input
                  type="date"
                  name="dateTo"
                  value={filters.dateTo}
                  onChange={handleFilterChange}
                  className="w-1/2 p-2.5 sm:p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Main Grid */}
        <div className="w-full lg:w-3/4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredRequests.map((request) => (
              <div
                key={request._id}
                className="bg-white rounded-xl shadow-xl p-6 h-full flex flex-col justify-between border border-secondary/10 hover:shadow-2xl transition-shadow duration-300 cursor-pointer"
                onClick={() => navigate(`/waste-requests/${request._id}`, { state: { request } })}
              >
                <div className="relative">
                  <img
                    src={
                      request.attachments?.[0]
                        ? `${BASE_URL}${request.attachments[0]}`
                        : 'https://via.placeholder.com/300x200'
                    }
                    alt={request.title}
                    className="w-full h-48 object-cover rounded-lg mb-4"
                  />
                  <div className="absolute top-2 right-2 bg-secondary/80 text-white text-sm font-semibold px-3 py-1 rounded-full">
                    {request.quantity.toLocaleString()} {request.unit}
                  </div>
                </div>

                <div>
                  <h2 className="text-primary text-xl sm:text-2xl font-semibold mb-2">
                    {request.title}
                  </h2>
                  <p className="text-gray-600 text-sm sm:text-base mb-2">
                    Status: <span className="capitalize font-medium text-secondary">Bidding</span>
                  </p>
                  <p className="text-gray-600 text-sm sm:text-base mb-2">
                    Location: {request.location?.city || 'N/A'}, {request.location?.country || 'N/A'}
                  </p>
                  <p className="text-gray-600 text-sm sm:text-base mb-2">
                    Deadline: {formatDate(request.deadline)}
                  </p>
                  <p className="text-secondary text-lg sm:text-xl font-medium mb-2">
                    Total Est. Value: ${request.totalEstimatedValue?.toFixed(2) || '0.00'}
                  </p>
                  <p className="text-gray-600 text-sm sm:text-base mb-2">
                    Est. Value per Unit: ${request.estimatedValuePerUnit?.toFixed(2) || '0.00'}/{request.unit}
                  </p>
                </div>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePlaceBid(request);
                  }}
                  className="w-full bg-secondary text-white px-4 py-2.5 sm:py-3 rounded-lg font-semibold text-sm sm:text-base hover:bg-opacity-90 transition duration-300 focus:outline-none focus:ring-2 focus:ring-secondary focus:ring-offset-2 shadow-sm"
                >
                  Place Bid
                </button>
              </div>
            ))}
          </div>

          {filteredRequests.length === 0 && (
            <p className="text-center text-gray-500 text-lg font-medium py-8">
              No bidding waste requests found.
            </p>
          )}
        </div>
      </div>

      {/* Modals */}
      <Modal
        isOpen={isLoginModalOpen}
        onClose={handleLoginClose}
        title="Login Required"
        className="bg-white rounded-lg shadow-xl p-4 sm:p-6 max-w-md mx-auto"
      >
        <p className="text-gray-600 text-sm sm:text-base mb-4">
          Please log in to place a bid on a waste request.
        </p>
        <button
          onClick={handleLoginClose}
          className="w-full bg-secondary text-white px-4 py-2.5 sm:py-3 rounded-lg hover:bg-opacity-90 transition duration-300 font-semibold"
        >
          Go to Login
        </button>
      </Modal>

      <Modal
        isOpen={isRoleModalOpen}
        onClose={handleRoleClose}
        title="Waste Collector Role Required"
        className="bg-white rounded-lg shadow-xl p-4 sm:p-6 max-w-md mx-auto"
      >
        <p className="text-gray-600 text-sm sm:text-base mb-4">
          Only waste collectors can place bids. Please sign up or update your profile to become a waste collector.
        </p>
        <div className="flex justify-between space-x-4">
          <button
            onClick={handleRoleClose}
            className="flex-1 bg-gray-400 text-white px-4 py-2.5 sm:py-3 rounded-lg hover:bg-gray-500 transition"
          >
            Cancel
          </button>
          <button
            onClick={handleRoleRedirect}
            className="flex-1 bg-secondary text-white px-4 py-2.5 sm:py-3 rounded-lg hover:bg-opacity-90 transition"
          >
            Update Profile
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default WasteRequests;